/**
 * SmartTalk — conversa global do usuário.
 *
 * A conversa pertence ao usuário do token, então nenhuma chamada envia id_usuario:
 * a API resolve o dono pelo próprio token. Anexar, desanexar e limpar devolvem o
 * estado completo, evitando um GET extra depois de cada ação.
 */

import * as http from '../helpers/http';

export interface ISmartTalkMensagem {
    id: string;
    sender: 'user' | 'ai';
    text: string;
    timestamp: string;
}

export interface ISmartTalkLei {
    id_proposicao: number;
    descricao: string;
    casa: number;
    casa_nome: string;
}

export interface ISmartTalkConversa {
    messages: ISmartTalkMensagem[];
    leis: ISmartTalkLei[];
    max_leis: number;
}

export interface ISmartTalkProposicaoBusca {
    id: number;
    descricao: string;
    casa: number;
    casa_nome: string;
    ementa: string | null;
}

class SmartTalkService {

    GetConversa = async (): Promise<ISmartTalkConversa | undefined> => {
        const result = await http.get('/smart-talk/conversa');
        if (result.success) return result.data;
        return undefined;
    };

    Interagir = async (userPrompt: string): Promise<{ resposta: string } | undefined> => {
        const result = await http.post('/smart-talk/conversa/interacao', { userPrompt });
        if (result.success) return result.data;
        // A API explica o motivo em { message } — IA desligada, sem crédito, etc.
        // Descartar isso deixava o usuário só com um "sem resposta" genérico.
        throw new Error((result as any)?.data?.message || 'Não foi possível obter resposta da IA.');
    };

    AnexarLei = async (id_proposicao: number): Promise<ISmartTalkConversa | undefined> => {
        const result = await http.post('/smart-talk/conversa/leis', { id_proposicao });
        if (result.success) return result.data;
        // A API responde 400 com { message } — teto atingido, lei repetida, sem texto.
        throw new Error((result as any)?.data?.message || 'Não foi possível anexar a lei.');
    };

    DesanexarLei = async (id_proposicao: number): Promise<ISmartTalkConversa | undefined> => {
        const result = await http.del(`/smart-talk/conversa/leis/${id_proposicao}`);
        if (result.success) return result.data;
        throw new Error((result as any)?.data?.message || 'Não foi possível remover a lei.');
    };

    LimparConversa = async (): Promise<ISmartTalkConversa | undefined> => {
        const result = await http.del('/smart-talk/conversa');
        if (result.success) return result.data;
        throw new Error((result as any)?.data?.message || 'Não foi possível limpar a conversa.');
    };

    BuscarProposicoes = async (descricao: string): Promise<ISmartTalkProposicaoBusca[]> => {
        const result = await http.get(`/smart-talk/proposicoes?descricao=${encodeURIComponent(descricao)}`);
        if (result.success) return result.data || [];
        return [];
    };
}

export default new SmartTalkService();
